import { cn } from '@/lib/cn';

/**
 * Toggle Kanban/Calendar/Lista — vista da page (R5.1 Bloco D / FEAT-V2-006).
 *
 * Segmented control acessível com data-testid. Usado em ManutencaoPage
 * (R5.1) e disponível pra futuros workflows que queiram triple-view.
 */

export type KanbanViewMode = 'kanban' | 'calendar' | 'list';

interface KanbanViewToggleProps {
  value: KanbanViewMode;
  onChange: (mode: KanbanViewMode) => void;
  /** Modos a esconder (ex: ['calendar'] em workflow sem data agendada). */
  hide?: KanbanViewMode[];
  className?: string;
}

const OPTIONS: Array<{ value: KanbanViewMode; label: string; icon: string }> = [
  { value: 'kanban', label: 'Kanban', icon: '▦' },
  { value: 'calendar', label: 'Calendário', icon: '📅' },
  { value: 'list', label: 'Lista', icon: '☰' },
];

export function KanbanViewToggle({ value, onChange, hide, className }: KanbanViewToggleProps) {
  const visible = OPTIONS.filter((o) => !hide?.includes(o.value));
  return (
    <div
      role="group"
      aria-label="Modo de visualização"
      data-testid="kanban-view-toggle"
      className={cn(
        'inline-flex items-center rounded-pill border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface p-0.5',
        className
      )}
    >
      {visible.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`view-toggle-${opt.value}`}
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'px-3 py-1 text-xs rounded-pill transition-colors flex items-center gap-1.5',
              active
                ? 'bg-red text-white'
                : 'text-ink-2 dark:text-ink-dark-2 hover:bg-paper dark:hover:bg-paper-dark-warm'
            )}
          >
            <span aria-hidden="true">{opt.icon}</span>
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
